import React, { useState, useMemo } from "react";
import { activities } from "@data/activities";
import type { ActivityItem } from "../types/index";
import { Link } from "react-router-dom";
import SEO from "@components/SEO";
import { createBreadcrumbStructuredData } from "../utils/structuredData";

const ActivitiesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const categories = useMemo(() => {
    const cats = [
      "all",
      ...new Set(activities.flatMap((activity) => activity.category)),
    ];
    return cats;
  }, []);

  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      const matchesCategory =
        selectedCategory === "all" ||
        activity.category.includes(selectedCategory);
      const matchesSearch =
        activity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.location.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchTerm]);

  const ActivityCard: React.FC<{ activity: ActivityItem }> = ({ activity }) => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
      {/* Activity Image */}
      <div className="relative h-48 overflow-hidden">
        <Link to={activity.link} target="_blank">
          <img
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}${activity.image}`}
            alt={activity.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="absolute top-4 left-4 flex flex-wrap gap-1">
          {activity.category.map((cat, index) => (
            <span
              key={index}
              className="bg-primary text-white px-2 py-1 rounded-full text-xs font-semibold"
            >
              {cat}
            </span>
          ))}
        </div>

        {activity.price === 0 && (
          <div className="absolute top-4 right-4 bg-secondary text-primary px-3 py-1 rounded-full text-sm font-semibold">
            Free
          </div>
        )}
      </div>

      {/* Activity Content */}
      <div className="p-6">
        <Link
          to={activity.link}
          target="_blank"
          className="text-xl font-bold text-primaryText group-hover:text-primary transition-colors"
        >
          {activity.name}
        </Link>

        <p className="text-gray-600 mt-2 mb-4">{activity.description}</p>

        {/* Activity Details */}
        <div className="flex flex-col text-sm">
          <div className="flex items-center text-primaryText">
            <svg
              className="w-4 h-4 mr-2 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {activity.location}
          </div>
          <div className="flex items-center text-primaryText">
            <svg
              className="w-4 h-4 mr-2 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {activity.duration}
          </div>
        </div>

        {/* Included Items */}
        {/* <div className="mb-4">
          <h4 className="text-sm font-semibold text-primaryText mb-2">
            Included:
          </h4>
          <div className="flex flex-wrap gap-1">
            {activity.included.slice(0, 3).map((item, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
              >
                {item}
              </span>
            ))}
            {activity.included.length > 3 && (
              <span className="text-gray-500 text-xs">
                +{activity.included.length - 3} more
              </span>
            )}
          </div>
        </div> */}

        {/* Price and CTA */}
        {/* <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-primary">
              {typeof activity.price === "number"
                ? `$${activity.price}`
                : activity.price}
            </span>
            <span className="text-gray-500 text-sm">
              {typeof activity.price === "number" ? "/person" : ""}
            </span>
          </div>
          <button className="bg-primary text-primaryText px-4 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-colors">
            Book Now
          </button>
        </div> */}
      </div>
    </div>
  );

  return (
    <div>
      <SEO
        title="New Zealand Activities & Adventures | Ikhlas Tours"
        description="Discover top New Zealand activities and adventures. From bungy jumping in Queenstown to scenic flights and Maori cultural tours, all bookable with Muslim-friendly options."
        path="/activities"
        image="/assets/queenstown-paraglide.jpg"
        structuredData={createBreadcrumbStructuredData([
          { name: "Home", url: "/" },
          { name: "Activities", url: "/activities" },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Activities & Experiences
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            Discover New Zealand's most thrilling adventures and cultural
            experiences
          </p>
        </div>
      </section>

      {/* Activities Description */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            Discover popular activities and experiences on offer across New
            Zealand. We specialise in customised travel - if there's something
            specific you'd like to do that isn't listed here, just let us know
            and we'll work to arrange it for you.
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="w-full lg:w-1/2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <div className="w-full lg:w-1/2">
              <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() =>
                      setSelectedCategory(
                        selectedCategory === category ? "all" : category,
                      )
                    }
                    className={`cursor-pointer px-4 py-2 rounded-md font-semibold transition-colors ${
                      selectedCategory === category
                        ? "bg-primary text-white"
                        : "bg-gray-200 text-primaryText hover:bg-gray-300"
                    }`}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-primaryText mb-2">
                No activities found
              </h3>
              <p className="text-gray-600">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <p className="text-gray-600">
                  Showing {filteredActivities.length} of {activities.length}{" "}
                  activities
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredActivities.map((activity) => (
                  <ActivityCard key={activity.id} activity={activity} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Activity Categories Overview */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primaryText mb-4">
              Explore by Category
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Find the perfect activity based on your interests and adventure
              level
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories
              .filter((cat) => cat !== "all")
              .map((category) => {
                const categoryActivities = activities.filter(
                  (a) => a.category === category
                );
                const categoryCount = categoryActivities.length;

                return (
                  <div
                    key={category}
                    className="text-center p-6 bg-gray-50 rounded-lg hover:shadow-md transition-shadow duration-300"
                  >
                    <h3 className="text-xl font-semibold text-primaryText mb-2 capitalize">
                      {category}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {categoryCount}{" "}
                      {categoryCount === 1 ? "activity" : "activities"}{" "}
                      available
                    </p>
                    <button
                      onClick={() => setSelectedCategory(selectedCategory === category ? "all" : category)}
                      className="text-primary font-semibold hover:text-secondary transition-colors"
                    >
                      View {category} Activities →
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Ready for Adventure?
          </h2>
          <p className="text-xl text-primaryText mb-8">
            Let our team help you choose and book the perfect activities for
            your New Zealand journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Plan Your Activities
            </a>
            <button
              onClick={() =>
                window.open(
                  "https://wa.me/64221841601?text=Hi! I'm interested in booking some activities.",
                  "_blank",
                )
              }
              className="inline-block bg-whatsapp cursor-pointer text-white px-8 py-3 rounded-md font-semibold hover:bg-green-600 transition-all duration-300"
            >
              WhatsApp Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ActivitiesPage;
